import React, { useState, useEffect, useCallback } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Avatar from '@mui/material/Avatar';
import Snackbar from '@mui/material/Snackbar';
import StorefrontIcon from '@mui/icons-material/Storefront';
import PageShell from '../components/PageShell';
import request, { uploadStoreLogo } from '../api';

function StoreSettings() {
    const [token] = useState(localStorage.getItem('storeToken'));
    const storeName = localStorage.getItem('storeName');
    const [store, setStore] = useState(null);
    const [name, setName] = useState('');
    const [address, setAddress] = useState('');
    const [phone, setPhone] = useState('');
    const [email, setEmail] = useState('');
    const [deliveryLocation, setDeliveryLocation] = useState('');
    const [logoUrl, setLogoUrl] = useState('');
    const [logoUploading, setLogoUploading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [snackbar, setSnackbar] = useState('');
    const navigate = useNavigate();

    const loadStore = useCallback(() => {
        if (!token) return;
        request('/store/me', { token })
            .then((data) => {
                setStore(data);
                setName(data.name || '');
                setAddress(data.address || '');
                setPhone(data.phone || '');
                setEmail(data.email || '');
                setDeliveryLocation(data.delivery_location || '');
                setLogoUrl(data.logo_url || '');
            })
            .catch(() => {
                localStorage.removeItem('storeToken');
                navigate('/store/login');
            });
    }, [token, navigate]);

    useEffect(() => {
        if (!token) {
            navigate('/store/login');
            return;
        }
        loadStore();
    }, [token, navigate, loadStore]);

    const logout = () => {
        localStorage.removeItem('storeToken');
        localStorage.removeItem('storeName');
        navigate('/store/login');
    };

    const handleLogoChange = async (e) => {
        const file = e.target.files[0];
        e.target.value = '';
        if (!file) return;
        setError('');
        setLogoUploading(true);
        try {
            const updated = await uploadStoreLogo(file, token);
            setLogoUrl(updated.logo_url || '');
        } catch (err) {
            setError(err.message);
        } finally {
            setLogoUploading(false);
        }
    };

    const submitProfile = async (e) => {
        e.preventDefault();
        setError('');
        setSaving(true);
        try {
            const updated = await request('/store/me', {
                method: 'PATCH',
                token,
                body: { name, address, phone, email, delivery_location: deliveryLocation },
            });
            setStore(updated);
            setSnackbar('Datos de tienda actualizados');
        } catch (err) {
            setError(err.message);
        } finally {
            setSaving(false);
        }
    };

    const nav = (
        <>
            <Button component={RouterLink} to="/store/dashboard" color="inherit">Volver al Panel</Button>
            <Button onClick={logout} variant="outlined" color="inherit">Cerrar Sesión</Button>
        </>
    );

    if (!store) {
        return (
            <PageShell title="Datos de Tienda" nav={nav}>
                <Typography color="text.secondary">Cargando...</Typography>
            </PageShell>
        );
    }

    return (
        <PageShell title={`Datos de Tienda${storeName ? `: ${storeName}` : ''}`} nav={nav} maxWidth="sm">
            <Stack alignItems="center" spacing={1.5} sx={{ mb: 5 }}>
                <Avatar
                    variant="rounded"
                    src={logoUrl || undefined}
                    sx={{ width: 80, height: 80, bgcolor: 'rgba(15,23,42,0.06)' }}
                >
                    <StorefrontIcon />
                </Avatar>
                <Button component="label" variant="outlined" size="small" disabled={logoUploading}>
                    {logoUploading ? 'Subiendo…' : logoUrl ? 'Cambiar Logo' : 'Subir Logo'}
                    <input type="file" accept="image/*" hidden onChange={handleLogoChange} />
                </Button>
            </Stack>

            <Paper sx={{ p: { xs: 2.5, sm: 3 } }}>
                <Typography variant="h6" gutterBottom>Información de la Tienda</Typography>
                <Box component="form" onSubmit={submitProfile}>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <TextField
                                label="Nombre de la tienda"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                fullWidth
                                required
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                label="Dirección"
                                value={address}
                                onChange={(e) => setAddress(e.target.value)}
                                fullWidth
                                required
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                label="Lugar de entrega"
                                value={deliveryLocation}
                                onChange={(e) => setDeliveryLocation(e.target.value)}
                                helperText="Opcional. Indícalo solo si difiere de la dirección de la tienda."
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Teléfono"
                                value={phone}
                                onChange={(e) => setPhone(e.target.value)}
                                fullWidth
                                required
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Correo electrónico"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                fullWidth
                                required
                            />
                        </Grid>
                        {error && (
                            <Grid item xs={12}>
                                <Alert severity="error">{error}</Alert>
                            </Grid>
                        )}
                        <Grid item xs={12}>
                            <Button type="submit" variant="contained" size="large" disabled={saving} fullWidth>
                                {saving ? 'Guardando…' : 'Guardar Cambios'}
                            </Button>
                        </Grid>
                    </Grid>
                </Box>
            </Paper>

            <Snackbar
                open={!!snackbar}
                autoHideDuration={4000}
                onClose={() => setSnackbar('')}
                message={snackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            />
        </PageShell>
    );
}

export default StoreSettings;
